/**
 * cockpit_toast — unified, low-risk toast surface (AWK tone).
 *
 * Presentation-only helper that replaces ad-hoc `alert()` for trivial,
 * non-blocking notices. It mounts a singleton container on first use and
 * appends self-dismissing toasts. No data wiring; safe to call from anywhere.
 *
 * NOTE: control-handshake / safety-critical confirmations still use the
 * existing ConfirmActionModal flow — do NOT route those through here.
 */

const TOAST_CONTAINER_ID = "aw-cockpit-toastwrap";
const TOAST_LIFETIME_MS = 2800;
const TOAST_FADE_MS = 280;

function ensureToastContainer(): HTMLElement | null {
  if (typeof document === "undefined") {
    return null;
  }

  let container = document.getElementById(TOAST_CONTAINER_ID);
  if (!container) {
    container = document.createElement("div");
    container.id = TOAST_CONTAINER_ID;
    container.className = "aw-toastwrap";
    document.body.appendChild(container);
  }

  return container;
}

export interface CockpitToastOptions {
  /** Render with the danger/alert tone (red). */
  alert?: boolean;
}

export function showCockpitToast(
  message: string,
  options: CockpitToastOptions = {},
): void {
  const container = ensureToastContainer();
  if (!container) {
    return;
  }

  const toast = document.createElement("div");
  toast.className = `aw-toast${options.alert ? " alert" : ""}`;
  toast.setAttribute("role", "status");
  toast.textContent = message;
  container.appendChild(toast);

  window.setTimeout(() => {
    toast.style.opacity = "0";
    toast.style.transition = `opacity ${TOAST_FADE_MS}ms ease`;
    window.setTimeout(() => {
      toast.remove();
    }, TOAST_FADE_MS);
  }, TOAST_LIFETIME_MS);
}

export default showCockpitToast;
